import {
	domAnimation,
	type FeatureBundle,
	type LazyFeatureBundle,
	LazyMotion,
	type MotionValue,
} from 'motion/react';
import * as m from 'motion/react-m';
import type * as React from 'react';
import { Component } from './utils';
import { withVariants } from './with-variants';

const MotionComponent = withVariants(m.create(Component));

const lazy = (load: () => Promise<{ default: FeatureBundle }>) => () =>
	load().then((r) => r.default);

const loaders = {
	domAnimation,
	domMax: lazy(() => import('./features/dom-max')),
} satisfies Record<string, FeatureBundle | LazyFeatureBundle>;

export type MotionFeature = keyof typeof loaders;

type MotionProviderProps = React.PropsWithChildren<{
	feature?: MotionFeature;
}>;

export function MotionProvider({
	children,
	feature = 'domAnimation',
}: MotionProviderProps) {
	return (
		<LazyMotion features={loaders[feature]} strict>
			{children}
		</LazyMotion>
	);
}

type MotionComponentProps = React.ComponentProps<typeof MotionComponent>;

type MotionProps<C extends React.ElementType = 'div'> = {
	as?: C;
	feature?: MotionFeature;
	type?: React.ButtonHTMLAttributes<HTMLButtonElement>['type'];
} & Omit<MotionComponentProps, 'as' | 'feature' | 'type'> & {
		[K in keyof Omit<
			React.ComponentPropsWithoutRef<C>,
			'as' | keyof MotionComponentProps
		>]: // NOTE: Mirrors motion's `AnyMotionValue` so any prop (e.g. svg `pathLength`)
		// can receive a `MotionValue`. `MotionValue` is invariant, so the exact element
		// type cannot be preserved; `any` matches the library's own typing.
		React.ComponentPropsWithoutRef<C>[K] | MotionValue<any>;
	};

export function Motion<C extends React.ElementType = 'div'>({
	feature = 'domAnimation',
	...props
}: MotionProps<C>) {
	return (
		<MotionProvider feature={feature}>
			<MotionComponent {...(props as unknown as MotionComponentProps)} />
		</MotionProvider>
	);
}
